import React, { useState, useEffect, useCallback } from "react";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import DatePicker from "./DatePicker";
import { Controller } from "react-hook-form";
import Modal from "./Modal";

const parseValueToDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;
  const str = String(value).trim();
  if (str.includes("-")) {
    const parts = str.split("-");
    if (parts[0].length === 4) {
      // YYYY-MM-DD
      const date = new Date(str);
      return isNaN(date.getTime()) ? null : date;
    } else {
      // DD-MM-YYYY
      const [day, month, year] = parts;
      const date = new Date(`${year}-${month}-${day}`);
      return isNaN(date.getTime()) ? null : date;
    }
  } else if (str.includes(".")) {
    // DD.MM.YYYY
    const [day, month, year] = str.split(".");
    const date = new Date(`${year}-${month}-${day}`);
    return isNaN(date.getTime()) ? null : date;
  }
  const date = new Date(str);
  return isNaN(date.getTime()) ? null : date;
};

const SearchableSelect = ({
  field,
  options,
  label,
  placeholder,
  readOnly,
  inputClass,
  onChangeOverride,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  // What the text box shows for a stored value.
  //
  // A value with no matching option is still the record's value, and in VIEW
  // mode the operator must be able to read it. Blanking it made 723 loans
  // display "Select Interest Rate..." over a rate sitting in the database —
  // either because the option was commented out later (1.61, 24, 22 in
  // utils.js) or because it never was one (36 computed annual percentages:
  // 19.32, 18.5338, 17.0133...).
  //
  // Deliberately NOT applied when the field is editable. There, an unmatched
  // value is one the operator cannot legitimately pick, and showing it as
  // though it were selected would invite saving a rate that is not on the rate
  // card. Entry keeps the old behaviour — blank, so a real choice must be made.
  const displayFor = useCallback(
    (value) => {
      const opt = options.find((o) => String(o.value) === String(value));
      if (opt) return opt.label;
      if (!readOnly) return "";
      return value === null || value === undefined ? "" : String(value);
    },
    [options, readOnly]
  );

  // Sync searchQuery with field.value
  useEffect(() => {
    setSearchQuery(displayFor(field.value));
  }, [field.value, displayFor]);

  const filteredOptions = options.filter((opt) =>
    String(opt.label).toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div
      className="relative w-full"
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) {
          setIsOpen(false);
          setSearchQuery(displayFor(field.value));
        }
      }}
    >
      <div className="relative flex items-center">
        <input
          type="text"
          className={[inputClass, "pr-10"].join(" ")}
          placeholder={placeholder || `Select ${label}...`}
          disabled={readOnly}
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            setIsOpen(true);
            // Clear the value in react-hook-form if they clear the input
            if (!e.target.value) {
              field.onChange("");
              if (onChangeOverride) onChangeOverride({ target: { value: "" } });
            }
          }}
          onFocus={() => {
            if (!readOnly) setIsOpen(true);
          }}
        />
        <div className="absolute right-3.5 top-1/2 -translate-y-1/2 pointer-events-none text-ink-400">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth="2"
            stroke="currentColor"
            className="w-4 h-4"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M19.5 8.25l-7.5 7.5-7.5-7.5"
            />
          </svg>
        </div>
      </div>

      {isOpen && !readOnly && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-border-light rounded-lg shadow-lg max-h-60 overflow-y-auto">
          {filteredOptions.length > 0 ? (
            filteredOptions.map((opt) => (
              <button
                key={opt.value}
                type="button"
                className={`w-full text-left px-3.5 py-2.5 text-sm transition-colors duration-150 hover:bg-primary/5
                  ${String(field.value) === String(opt.value)
                    ? "bg-primary/10 text-primary font-semibold"
                    : "text-ink-700"
                  }`}
                onMouseDown={() => {
                  field.onChange(opt.value);
                  setSearchQuery(opt.label);
                  setIsOpen(false);
                  if (onChangeOverride) {
                    onChangeOverride({ target: { value: opt.value } });
                  }
                }}
              >
                {opt.label}
              </button>
            ))
          ) : (
            <div className="px-3.5 py-2.5 text-sm text-ink-400 italic">
              No options found
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const FormInput = ({
  label,
  type = "text",
  name,
  register,
  errors,
  readOnly,
  handleFileChange,
  existingFileUrl,
  filePreview,
  required,
  placeholder,
  control,
  minDate,
  options = [],
  popperPlacement = "right-start",
  // Letters-only typing. Opt-in per field, never by type: 136 fields here are
  // `type: "text"` and many must accept digits — PAN No, IFSC Code, Door Number,
  // ID Proof No, Email. Destructured so it is not forwarded to the DOM as an
  // unknown attribute.
  alphaOnly,
  // Date value format for type="datepicker" — "iso" (default) or "dot".
  // Destructured for the same reason as alphaOnly: it must not reach the DOM.
  valueFormat,
  ...props
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  // The name of a file the operator has just picked in THIS session.
  // A file input cannot be read back after render, so it is captured on change.
  const [pickedFileName, setPickedFileName] = useState("");
  const errorMsg = errors?.[name]?.message;

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const registerProps = register ? register(name) : {};

  // Custom border-embedded label input class (matching primary theme colors)
  const inputClass = [
    "form-input bg-white rounded-lg border w-full px-3.5 py-2.5 text-sm transition-all duration-150 focus:outline-none focus:ring-1",
    errorMsg
      ? "border-danger-500 focus:border-danger-500 focus:ring-danger-500/20 text-danger-900"
      : "border-border-medium hover:border-primary focus:border-primary focus:ring-primary/20 text-ink-700",
    readOnly ? "bg-[#F8FAFC] border-border text-ink-400 cursor-not-allowed" : ""
  ].join(" ");

  // Standard input elements wrapper
  const renderInput = () => {
    switch (type) {
      case "checkbox":
        return (
          <label
            htmlFor={name}
            className={`inline-flex items-center gap-3 px-4 py-2.5 rounded-xl border cursor-pointer select-none transition-colors duration-150
              ${readOnly
                ? "bg-[#F8FAFC] border-border cursor-not-allowed opacity-70"
                : "bg-white border-border-medium hover:border-primary hover:bg-primary/5"
              }`}
          >
            <input
              id={name}
              type="checkbox"
              className="w-4 h-4 rounded accent-primary cursor-pointer"
              disabled={readOnly}
              {...registerProps}
              {...props}
            />
            {label && (
              <span className="text-sm font-semibold text-ink-700">
                {label}
              </span>
            )}
          </label>
        );

      case "select":
        if (!control) return <input id={name} type="text" className={inputClass} placeholder={placeholder} readOnly={readOnly} {...registerProps} {...props} />;
        return (
          <Controller
            control={control}
            name={name}
            render={({ field }) => (
              <SearchableSelect
                field={field}
                options={options}
                label={label}
                placeholder={placeholder}
                readOnly={readOnly}
                inputClass={inputClass}
                onChangeOverride={props.onChange}
              />
            )}
          />
        );

      case "textarea":
        return (
          <textarea
            id={name}
            className={inputClass}
            placeholder={placeholder}
            readOnly={readOnly}
            {...registerProps}
            {...props}
          />
        );

      case "file":
        const hasFile = !!(filePreview || existingFileUrl);

        // The name to show beneath the picker.
        //
        // A browser will NOT let a page pre-fill <input type="file"> — it is a
        // security rule, not a bug: a page that could set the value could steal
        // a file off the machine. So the control always reads "No file chosen"
        // even when a document IS on record, and operators reasonably read that
        // as "my upload was lost".
        //
        // Uploads are stored as `<timestamp>-<random>-<originalname>`
        // (addDetailsWithImages.js:22-31), so the name the operator recognises
        // is recoverable by dropping those two generated segments.
        const storedFileName = existingFileUrl
          ? (() => {
            const raw = decodeURIComponent(
              String(existingFileUrl).split("/").pop().split("?")[0]
            );
            const m = raw.match(/^\d{10,}-\d+-(.+)$/);
            return m ? m[1] : raw;
          })()
          : "";
        // A file just picked wins — it is what will be saved on Update.
        const shownFileName = pickedFileName || storedFileName;

        // What the BOX says. The native control draws its own "No file chosen"
        // beside the button, and that text is not reachable from CSS or JS —
        // the `file:` variants restyle the button only. Since a page may not
        // pre-fill a file input, the native control reads "No file chosen" even
        // over a document that uploaded perfectly well, and the operator reads
        // the box rather than any note beneath it. So the native control is
        // visually hidden below and this replaces its text.
        const statusText = shownFileName || "No file chosen";

        return (
          <div className="relative w-full">
            {!readOnly ? (
              <div
                className={[
                  inputClass,
                  "flex items-center gap-2 text-xs px-2 py-1.5",
                  hasFile ? "pr-10" : "",
                  // The focus ring lived on the native control, which is now
                  // hidden — move it to the wrapper so the field still shows
                  // where the keyboard is.
                  "focus-within:border-primary focus-within:ring-1 focus-within:ring-primary/20"
                ].join(" ")}
              >
                {/* Visually hidden, NOT display:none — a display:none input is
                    removed from the tab order and the field stops being
                    keyboard-reachable. htmlFor on the label opens the dialog
                    natively, with no synthetic click. */}
                <input
                  id={name}
                  type="file"
                  className="absolute w-px h-px opacity-0 overflow-hidden"
                  onChange={(e) => {
                    setPickedFileName(e.target.files?.[0]?.name || "");
                    handleFileChange?.(e);
                  }}
                  {...props}
                />
                <label
                  htmlFor={name}
                  className="shrink-0 py-0.5 px-2 rounded text-2xs font-semibold bg-primary/10 text-primary hover:bg-primary/20 transition-colors duration-150 cursor-pointer"
                >
                  Choose File
                </label>
                <span
                  className={[
                    "min-w-0 truncate",
                    // Filled: inherit the wrapper's ink-700, just weighted.
                    // Empty: ink-300 is the declared placeholder colour, so the
                    // field still reads as unfilled rather than as a value.
                    shownFileName ? "font-medium" : "text-ink-300"
                  ].join(" ")}
                  title={shownFileName || undefined}
                >
                  {statusText}
                </span>
              </div>
            ) : (
              <input
                type="text"
                readOnly
                className={inputClass}
                // The name rather than a bare "Document Uploaded": with two ID
                // proof fields side by side, the name is what tells the operator
                // WHICH document is on each.
                value={
                  hasFile ? (shownFileName || "Document Uploaded") : "No Document"
                }
                title={shownFileName || undefined}
              />
            )}
            {hasFile && (
              <button
                type="button"
                onClick={() => setIsPreviewOpen(true)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-primary hover:text-primary-hover focus:outline-none transition-colors duration-150"
                title="Preview document"
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" className="w-4 h-4">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
                </svg>
              </button>
            )}
            {/* Only for a file picked in THIS session. The stored-file case used
                to appear here too, but the box above now carries the filename
                itself, so repeating it read as a contradiction beside the
                browser's "No file chosen". This says the one thing the filename
                alone does not: nothing is saved until Update. */}
            {!readOnly && pickedFileName && (
              <p className="mt-1 flex items-start gap-1 text-2xs leading-snug text-ink-500">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  className="w-3 h-3 mt-px shrink-0 text-success-600"
                  aria-hidden="true"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
                </svg>
                <span className="min-w-0">Will be saved on Update</span>
              </p>
            )}
            <Modal
              isOpen={isPreviewOpen}
              onClose={() => setIsPreviewOpen(false)}
              title={label || "Document Preview"}
              size="md"
            >
              <div className="flex justify-center items-center">
                <img
                  src={filePreview || existingFileUrl}
                  alt={label || "Document preview"}
                  className="w-[300px] h-[180px] object-contain rounded-lg"
                />
              </div>
            </Modal>
          </div>
        );

      case "password":
        return (
          <div className="relative flex items-center w-full">
            <input
              id={name}
              type={showPassword ? "text" : "password"}
              className={inputClass}
              placeholder={placeholder}
              readOnly={readOnly}
              {...registerProps}
              {...props}
            />
            <button
              type="button"
              onClick={togglePasswordVisibility}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-400 hover:text-ink-600 focus:outline-none"
            >
              {showPassword ? <FaEye className="w-4 h-4" /> : <FaEyeSlash className="w-4 h-4" />}
            </button>
          </div>
        );

      case "datepicker":
        if (!control) return <input id={name} type="text" className={inputClass} placeholder={placeholder} readOnly={readOnly} {...registerProps} {...props} />;
        return (
          <Controller
            control={control}
            name={name}
            render={({ field }) => (
              <DatePicker
                selected={field.value}
                onChange={(dateStr) => {
                  field.onChange(dateStr);
                }}
                minDate={minDate}
                className="w-full"
                wrapperClassName="w-full"
                placeholder={placeholder}
                readOnly={readOnly}
                disabled={readOnly}
                popperPlacement={popperPlacement}
                valueFormat={valueFormat}
              />
            )}
          />
        );

      default:
        let inputType = type;
        let maxLength = props.maxLength;
        let inputMode = props.inputMode;
        let pattern = props.pattern;

        if (type === "number") {
          const lowerName = name.toLowerCase();
          if (lowerName.includes("mobile") || lowerName.includes("phone")) {
            inputType = "tel";
            maxLength = 10;
            inputMode = "numeric";
            pattern = "[0-9]*";
          } else if (lowerName.includes("postal") || lowerName.includes("pin")) {
            inputType = "tel";
            maxLength = 6;
            inputMode = "numeric";
            pattern = "[0-9]*";
          }
        }

        return (
          <input
            {...registerProps}
            {...props}
            id={name}
            // A focused number input changes value on mouse wheel by default, so
            // scrolling the page past one silently edits it — no click, no
            // keystroke, no warning. On a loan form those figures decide what is
            // disbursed. Blurring cancels the browser's change; the page still
            // scrolls normally, and non-number inputs are untouched.
            onWheel={(e) => {
              if (type === "number") e.currentTarget.blur();
            }}
            type={inputType}
            inputMode={inputMode}
            pattern={pattern}
            maxLength={maxLength}
            className={[
              inputClass,
              props.className,
              // Hide spin buttons on number inputs
              type === "number" ? "[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" : ""
            ].join(" ")}
            placeholder={placeholder}
            readOnly={readOnly}
            onClick={props.onClick}
            onChange={(e) => {
              if (inputType === "tel") {
                const clean = e.target.value.replace(/\D/g, "");
                e.target.value = maxLength ? clean.slice(0, maxLength) : clean;
              }
              // Mirror of the tel rule above, inverted. Apostrophe, hyphen and
              // full stop are kept deliberately — D'Souza, Mary-Anne and
              // K.Kumar are real names and must not be mangled.
              if (alphaOnly) {
                e.target.value = e.target.value.replace(/[^A-Za-z\s.'-]/g, "");
              }
              if (registerProps.onChange) {
                registerProps.onChange(e);
              }
              if (props.onChange) {
                props.onChange(e);
              }
            }}
          />
        );
    }
  };

  return (
    <div className={`relative w-full group ${type === "checkbox" ? "" : "mt-3 mb-1"}`}>
      {type !== "checkbox" && label && (
        <label
          htmlFor={name}
          className={[
            "absolute -top-2 left-3 bg-white px-1.5 text-xs font-bold tracking-wide transition-colors duration-150 select-none z-10 pointer-events-none",
            readOnly
              ? "text-ink-400"
              : errorMsg
                ? "text-danger-500"
                : "text-ink-400 group-hover:text-ink-900",
          ].join(" ")}
        >
          {label}
          {required && <span className="text-danger-500 ml-1">*</span>}
        </label>
      )}
      {renderInput()}
      {errorMsg && <p className="form-error-msg">{errorMsg}</p>}
    </div>
  );
};

export default FormInput;
